import {ChangeDetectionStrategy, Component, computed, input, InputSignal, Signal} from '@angular/core';
import {CommonModule} from '@angular/common';

import {DrawingModel, EventModel, EventRankingModel, GameModel, GroupRankingModel, TournamentModel} from 'shared/models';
import {EventOverviewUi} from '../event-overview-ui/event-overview-ui';
import {ProgressBarUi} from '../progress-bar-ui/progress-bar-ui';
import {GroupsUi} from '../groups/groups-ui/groups-ui';
import {DrawsUi} from '../draws/draws-ui/draws-ui';
import {EventRankingUi} from '../event-ranking-ui/event-ranking-ui';

@Component({
  selector: 'event-ui',
  standalone: true,
  imports: [CommonModule, GroupsUi, DrawsUi, EventRankingUi, ProgressBarUi, EventOverviewUi],
  templateUrl: './event-ui.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventUi {
  tournament: InputSignal<TournamentModel | undefined> = input<TournamentModel | undefined>();
  event: InputSignal<EventModel> = input.required<EventModel>();
  drawings: InputSignal<DrawingModel[]> = input.required<DrawingModel[]>();
  games: InputSignal<GameModel[]> = input.required<GameModel[]>();
  groupsRankings: InputSignal<GroupRankingModel[]> = input.required<GroupRankingModel[]>();
  eventsRankings: InputSignal<EventRankingModel[] | undefined> = input<EventRankingModel[] | undefined>();
  numbersQualifiersPerGroup: InputSignal<number | undefined> = input<number>();

  protected eventDrawings: Signal<DrawingModel[]> = computed((): DrawingModel[] => {
    const event: EventModel = this.event();
    const drawings: DrawingModel[] = this.drawings();
    return drawings.filter((model: DrawingModel): boolean => {
      return model.eventId === event.id;
    });
  });

  protected parentEventDrawings: Signal<DrawingModel[] | undefined> = computed((): DrawingModel[] | undefined => {
    const event: EventModel = this.event();
    if (!event.parentId) {
      return undefined;
    }

    const drawings: DrawingModel[] = this.drawings();
    return drawings.filter((model: DrawingModel): boolean => {
      return model.eventId === event.parentId;
    });
  });

  protected numberGroups: Signal<number | undefined> = computed((): number | undefined => {
    const parentEventDrawings: DrawingModel[] | undefined = this.parentEventDrawings();
    if (parentEventDrawings === undefined) {
      return undefined;
    }

    const drawings: DrawingModel[] = this.drawings();
    const groups: number[] = drawings.map((model: DrawingModel): number => {
      return model.group;
    });
    // Unique
    return [...new Set(groups)].length;
  });

  protected eventGames: Signal<GameModel[]> = computed((): GameModel[] => {
    const event: EventModel = this.event();
    const games: GameModel[] = this.games();
    return games.filter((model: GameModel): boolean => {
      return model.eventId === event.id;
    });
  });

  protected eventGroupsRankings: Signal<GroupRankingModel[]> = computed((): GroupRankingModel[] => {
    const event: EventModel = this.event();
    const groupsRankings: GroupRankingModel[] = this.groupsRankings();
    return groupsRankings.filter((model: GroupRankingModel): boolean => {
      return model.eventId === event.id;
    });
  });

  protected eventEventsRankings: Signal<EventRankingModel[]> = computed((): EventRankingModel[] => {
    const event: EventModel = this.event();
    const eventsRankings: EventRankingModel[] | undefined = this.eventsRankings();
    if (eventsRankings === undefined) {
      return [];
    }

    return eventsRankings.filter((model: EventRankingModel): boolean => {
      return model.eventId === event.id;
    });
  });
}
